/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Use, reproduction, modification, or distribution of this code
 * is permitted only pursuant to a written license expressly granted
 * by Alexander Orlov.
 *
 * You shall not disclose such confidential information and shall use
 * it only in accordance with the terms of the written license agreement
 * you have entered into with Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */


import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/store/authStore';
import { useAppStore } from '@/store/appStore';
import { isSeparatelyAuthenticated } from './authFailureScope';

// Always use proxy - client never knows about backend API
const API_BASE_URL = '/api';

// Debug logging
console.log('🔧 API Client Configuration:');
console.log('  Using proxy path:', API_BASE_URL);
console.log('  Backend configured on server via REACT_APP_API_URL environment variable');

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

// Request interceptor to add auth token and content-type
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Admin impersonation: send X-Owner-Id header when viewing another owner's data
    const { selectedOwner } = useAppStore.getState();
    const authUser = useAuthStore.getState().user;
    if (selectedOwner && authUser && selectedOwner.id !== authUser.id) {
      config.headers['X-Owner-Id'] = selectedOwner.id;
    }

    // Only set Content-Type for POST/PUT/PATCH requests, but not for FormData
    if (config.method && ['post', 'put', 'patch'].includes(config.method.toLowerCase())) {
      // Don't set Content-Type for FormData or if already explicitly set to undefined
      if (!(config.data instanceof FormData) && config.headers['Content-Type'] !== undefined) {
        config.headers['Content-Type'] = 'application/json-patch+json';
      }
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Public page paths where 401 should NOT trigger login redirect
// (driver-facing and owner-onboarding pages that use JWT from authKey, not owner session).
// Keep this list in sync with PUBLIC_PAGE_PATHS in hooks/useInactivityTimeout.ts.
const PUBLIC_PAGE_PATHS = [
  '/rental-agreement/',
  '/create-user-and-agreement/',
  '/create-user/',
  '/create-user',
  '/create-owner',
  '/owner-agreement/',
  '/add-card/',
  '/link-expired',
  '/terms',
  '/privacy',
  '/rental-terms',
];

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      const isPublicPage = PUBLIC_PAGE_PATHS.some(path => window.location.pathname.startsWith(path));
      const fromOtherService = isSeparatelyAuthenticated(error.config?.url);

      if (fromOtherService) {
        // Leave the session alone and let the caller deal with it. Logging the URL because the
        // alternative — a silent partial page — is how this went unnoticed for as long as it did.
        console.error(
          `401 from ${error.config?.url}, which authenticates separately. Session left intact; ` +
          'check that service\'s JWT issuer/audience configuration.'
        );
      } else if (!isPublicPage) {
        // Unauthorized - clear auth + app state and redirect to login
        useAuthStore.getState().logout();
        useAppStore.getState().reset();
        // Suppress beforeunload dialog — session expired
        window.onbeforeunload = null;
        window.location.href = '/login';
      }
    }
    // 503 — maintenance mode or deployment
    if (error.response?.status === 503) {
      const data = error.response.data as any;
      if (data?.maintenance) {
        const isPublicPage = PUBLIC_PAGE_PATHS.some(path => window.location.pathname.startsWith(path));
        if (!isPublicPage) {
          // Server is in maintenance mode — log out and redirect to login
          useAuthStore.getState().logout();
          useAppStore.getState().reset();
          window.onbeforeunload = null;
          window.location.href = '/login';
          return new Promise(() => {}); // never resolve — page is redirecting
        }
      }
      error.response.data = { error: 'Server is temporarily unavailable (maintenance). Please try again in a few minutes.' };
    }
    return Promise.reject(error);
  }
);

export default apiClient;
